/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";

import { Helmet } from "react-helmet-async";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import useProductParams from "@/src/hooks/useProductParams";
import { getProductData } from "@/src/services/productService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { AbstractCase, isProductClassName } from "@/src/types/Entities/AbstractCase";
import InitialClientDescriptionAndObjectives
  from "@/src/components/Form/PendingCase/InitialClientDescriptionAndObjectives";
import ApiClient from "@/src/services/apiClient";
import WaitingForAiResponse from "@/src/components/Form/Common/WaitingForAiResponse/WaitingForAiResponse";
import RefinementQuestions from "@/src/components/Form/PendingCase/RefinementQuestions";
import CoveredMatch from "@/src/components/Form/PendingCase/CoveredMatch";
import HardStop from "@/src/components/Form/PendingCase/HardStop";
import { translate } from "@/src/services/translations/translations";
import RefineEmploymentLaw from "@/src/components/Form/PendingCase/RefineEmploymentLaw";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import ScrollToTop from "@/src/services/ScrollToTop";

export default function MatchPendingCasePage() {
  const { productId } = useProductParams();
  const productApiUri = getProductData("pendingCase", "apiUri");
  const [isLoading, setIsLoading] = useState(false);
  const [pendingCaseType, setPendingCaseType] = useState<string | null>(null);
  const [needEmploymentLawRefinement, setNeedEmploymentLawRefinement] = useState<boolean | null>(null);
  const [isCovered, setIsCovered] = useState<boolean>(false);
  const hasProduct = Boolean(productApiUri && productId);
  const productQueryKey = queryKeys.item(productApiUri, productId);
  const [formVersion, setFormVersion] = useState(0);
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data);
      setPendingCaseType(null);
      setFormVersion((prev) => prev + 1);
    },
  });

  const {
    data: product,
    isLoading: isLoadingProduct,
    refetch,
  } = useQuery({
    queryKey: productQueryKey,
    queryFn: () => fetchResource(productApiUri!, productId!),
    enabled: hasProduct,
  });

  if (hasProduct && isLoadingProduct) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  const initialValues = {
    ...product,
  };

  const convertToFinalCase = async (productId: number, productType: string) => {
    await ApiClient.post("/services/pending_case/convert_to_final_case", {
      body: JSON.stringify({
        productId: productId,
        productType: productType,
      }),
    });
  };

  const handleSubmit = async ({ values }: { values: AbstractCase }) => {
    setIsLoading(true);
    setPendingCaseType(null);

    let clientIsCivilServant = null;

    if (values?.clientIsCivilServant) {
      clientIsCivilServant = values?.clientIsCivilServant === "yes";
    }

    const response = await ApiClient.post("/services/pending_case/determine_pending_case_type", {
      body: JSON.stringify({
        productId: product.id,
        text: values.initialClientDescriptionAndObjectives,
        refinementAnswers: values?.refinementAnswers,
        clientIsCivilServant: clientIsCivilServant,
      }),
    });

    setNeedEmploymentLawRefinement(response["needEmploymentLawRefinement"]);

    if (response["pendingCaseType"] === "ambiguousMatch" || (response["pendingCaseType"] === "unconfidentMatch" && product.aiRefinementQuestions === null)) {
      await ApiClient.post("/services/pending_case/generate_refinement_questions", {
        body: JSON.stringify({
          productId: product.id,
          text: values.initialClientDescriptionAndObjectives,
        }),
      });

      await refetch();
      setFormVersion((prev) => prev + 1);
    } else {
      if (
        response["isCovered"] === true &&
        response["pendingCaseType"] !== "ambiguousMatch" &&
        response["pendingCaseType"] !== "unconfidentMatch" &&
        response["needEmploymentLawRefinement"] === false
      ) {
        await convertToFinalCase(product.id, response["pendingCaseType"]);
      }

      setPendingCaseType(response["pendingCaseType"]);
      setIsCovered(response["isCovered"]);
    }

    setIsLoading(false);
  };

  const showLoading = isLoading || isLoadingProduct;

  let activeComponent = "start";

  if (pendingCaseType && ["settlement", "employmentContract", "civilServiceLaw"].includes(pendingCaseType) && needEmploymentLawRefinement) {
    activeComponent = "refineEmploymentLaw";
  } else if (pendingCaseType === "unconfidentMatch" && product.aiRefinementQuestions !== null) {
    activeComponent = pendingCaseType;
  } else if (pendingCaseType !== null && pendingCaseType !== "unconfidentMatch" && isCovered) {
    activeComponent = "covered";
  } else if (pendingCaseType !== null && pendingCaseType !== "unconfidentMatch" && !isCovered) {
    activeComponent = "uncovered";
  } else if (product.aiRefinementQuestions) {
    activeComponent = "refinement";
  }

  return (
    <LayoutProtectedRoutes>
      <Helmet>
        <title>Fallschilderung | Legalbird</title>
      </Helmet>
      {!showLoading && <ScrollToTop key={activeComponent} />}
      <FormProvider initialValues={initialValues} submitAction={handleSubmit} key={`${product.id}-${formVersion}`}>
        {!showLoading && activeComponent === "start" && (
          <InitialClientDescriptionAndObjectives
            product={product}
            submitProps={{
              isMinimal: true,
              hideBackButton: true,
            }}
          />
        )}
        {!showLoading && activeComponent === "refinement" && (
          <RefinementQuestions
            submitProps={{
              isMinimal: true,
            }}
            product={product}
            updateMutation={updateMutation}
          />
        )}

        {!showLoading && activeComponent === "refineEmploymentLaw" && (
          <RefineEmploymentLaw
            submitProps={{
              isMinimal: true,
            }}
            product={product}
            updateMutation={updateMutation}
          />
        )}

        {!showLoading && activeComponent === "covered" && pendingCaseType !== null && isProductClassName(pendingCaseType) && (
          <CoveredMatch product={product} productType={pendingCaseType} />
        )}
        {!showLoading && activeComponent === "uncovered" && <HardStop updateMutation={updateMutation} reason={"uncovered"} product={product} />}
        {!showLoading && activeComponent === "unconfidentMatch" && <HardStop updateMutation={updateMutation} reason={"unconfident"} product={product} />}
        {showLoading && (
          <WaitingForAiResponse
            mobileHeadline={translate(`${product.name}.loadingScreen.headline`, product)}
            headline={translate(`${product.name}.loadingScreen.headline`, product)}
            text={translate(`${product.name}.loadingScreen.text`, product)}
          />
        )}
      </FormProvider>
    </LayoutProtectedRoutes>
  );
}
